/**
 * API Configuration with Axios and JWT interceptors
 * Modern approach with automatic token refresh and error handling
 * Enhanced with request deduplication and retry logic
 */

import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios'
import { STORAGE_KEYS } from './storageKeys'
import {
  getAuthStorageItem,
  removeAuthStorageItem,
  setAuthStorageItem,
} from './authStorage'
import { tokenRefreshManager } from './tokenRefresh'
import { parseApiError, SERVER_UNAVAILABLE_EVENT } from './apiError'

// API Base Configuration
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001'

// Request deduplication cache
const requestCache = new Map<string, Promise<unknown>>()

export const METODOS_REPETIVEIS = new Set(['get', 'head', 'options'])

// Retry configuration
export const DEFAULT_RETRY_CONFIG = {
  retries: 3,
  retryDelay: 1000,
  retryCondition: (error: AxiosError) => {
    // Só método idempotente: repetir um POST que o servidor chegou a processar
    // (o 5xx pode vir depois do efeito) duplica mensagem de chat, e-mail e
    // proposta. O login também não repete: com o servidor fora, quem resolve é
    // o popup de ligar o servidor, não 7 s de espera.
    const metodo = (error.config?.method || 'get').toLowerCase()
    if (!METODOS_REPETIVEIS.has(metodo)) return false
    return (
      !error.response ||
      error.response.status >= 500 ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK'
    )
  },
}

// Helper to create cache key for requests (includes token hash for user isolation)
const createRequestKey = (config: AxiosRequestConfig): string => {
  const { method = 'GET', url = '', params, data } = config
  // Include a hash of the token to ensure cache isolation between users
  const token = getAuthStorageItem(STORAGE_KEYS.ACCESS_TOKEN) || ''
  const tokenHash = token ? token.slice(-8) : 'anonymous'
  return `${tokenHash}:${method.toUpperCase()}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`
}

// Helper for exponential backoff delay
const getRetryDelay = (retryCount: number, baseDelay: number): number => {
  return baseDelay * Math.pow(2, retryCount)
}

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token management utilities
export const tokenManager = {
  getAccessToken: (): string | null => {
    return getAuthStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
  },

  getRefreshToken: (): string | null => {
    return getAuthStorageItem(STORAGE_KEYS.REFRESH_TOKEN)
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    setAuthStorageItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken)
    setAuthStorageItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
  },

  clearTokens: (): void => {
    removeAuthStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
    removeAuthStorageItem(STORAGE_KEYS.REFRESH_TOKEN)
    removeAuthStorageItem(STORAGE_KEYS.USER)
  },

  isTokenExpired: (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp < currentTime
    } catch {
      return true
    }
  },
}

// Request interceptor with deduplication and auth token
api.interceptors.request.use(
  config => {
    // Add auth token
    const token = tokenManager.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Handle request deduplication for GET requests
    if (config.method?.toLowerCase() === 'get') {
      const requestKey = createRequestKey(config)

      if (requestCache.has(requestKey)) {
        // Return existing promise to deduplicate
        const existingPromise = requestCache.get(requestKey)!
        return Promise.reject({
          __DEDUPLICATED__: true,
          promise: existingPromise,
        })
      }
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Enhanced response interceptor with retry logic and deduplication handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Clear cache for successful requests to allow fresh data
    if (response.config.method?.toLowerCase() === 'get') {
      const requestKey = createRequestKey(response.config)
      requestCache.delete(requestKey)
    }
    return response
  },
  async (error: unknown) => {
    // Handle deduplication
    if (error && typeof error === 'object' && '__DEDUPLICATED__' in error) {
      return (error as unknown as { promise: Promise<unknown> }).promise
    }

    const axiosError = error as AxiosError
    const originalRequest = axiosError.config as typeof axiosError.config & {
      _retry?: boolean
      _retryCount?: number
    }

    // Initialize retry count
    originalRequest._retryCount = originalRequest._retryCount || 0

    // Do not redirect or refresh tokens on login errors
    const url = originalRequest?.url || ''
    if (
      axiosError.response?.status === 401 &&
      url.includes('/api/auth/login/')
    ) {
      return Promise.reject(axiosError)
    }

    // Handle 401 for token refresh
    if (axiosError.response?.status === 401 && !originalRequest._retry) {
      return tokenRefreshManager.handle401(originalRequest, API_BASE_URL)
    }

    // Handle retry logic for other errors
    if (
      originalRequest &&
      originalRequest._retryCount < DEFAULT_RETRY_CONFIG.retries &&
      DEFAULT_RETRY_CONFIG.retryCondition(axiosError)
    ) {
      originalRequest._retryCount++
      originalRequest._retry = true

      const delay = getRetryDelay(
        originalRequest._retryCount - 1,
        DEFAULT_RETRY_CONFIG.retryDelay
      )

      await new Promise(resolve => setTimeout(resolve, delay))

      return api(originalRequest)
    }

    // Clear failed requests from cache
    if (originalRequest?.method?.toLowerCase() === 'get') {
      const requestKey = createRequestKey(originalRequest)
      requestCache.delete(requestKey)
    }

    // Servidor fora (desligado pelo idle-check, ligando, ou caído): o guard
    // montado no layout abre o popup que diz o que está acontecendo e oferece
    // ligar. Sem isto, quem já estava logado via só telas vazias.
    // Timeout também pode ser servidor fora (a EC2 parada não recusa a conexão,
    // deixa pendurar): o guard confirma com o ping de saúde antes de abrir, para
    // uma consulta só lenta não virar "servidor desligado".
    const info = parseApiError(error)
    const source = info.serverUnavailable
      ? 'api'
      : axios.isAxiosError(error) &&
          (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT')
        ? 'timeout'
        : undefined
    if (source) {
      window.dispatchEvent(
        new CustomEvent(SERVER_UNAVAILABLE_EVENT, { detail: { source } })
      )
    }

    return Promise.reject(error)
  }
)

// Enhanced API wrapper with intelligent caching
const makeRequest = async <T = unknown>(
  config: AxiosRequestConfig
): Promise<T> => {
  const requestKey = createRequestKey(config)

  // For GET requests, check and cache
  if (config.method?.toLowerCase() === 'get') {
    if (requestCache.has(requestKey)) {
      return requestCache.get(requestKey) as Promise<T>
    }

    const requestPromise = api<T>(config).then(response => response.data)
    requestCache.set(requestKey, requestPromise)

    // Auto-cleanup cache after 5 minutes
    setTimeout(
      () => {
        requestCache.delete(requestKey)
      },
      5 * 60 * 1000
    )

    return requestPromise
  }

  // For non-GET requests, always make fresh request
  const response = await api<T>(config)
  return response.data
}

// Export enhanced API client
export { makeRequest }

// API endpoints
export const apiEndpoints = {
  auth: {
    login: '/api/auth/login/',
    refresh: '/api/auth/refresh/',
    logout: '/api/auth/logout/',
    profile: '/api/auth/profile/',
    resetPassword: '/api/auth/reset-password/',
    resetPasswordConfirm: '/api/auth/reset-password/confirm/',
  },
  agents: {
    available: '/api/agents/available/',
    snippets: (agentCode: string) => `/api/agents/${agentCode}/snippets/`,
  },
  chat: {
    sendMessage: '/api/agents/chat/',
    getResult: (jobId: string) => `/api/agents/chat/result/${jobId}/`,
    sessions: '/api/agents/chat/sessions/',
    history: (sessionId: string) =>
      `/api/agents/chat/sessions/${sessionId}/history/`,
    updateSession: (sessionId: string) =>
      `/api/agents/chat/sessions/${sessionId}/`,
    // Sharing endpoints
    shares: (sessionId: string) =>
      `/api/agents/chat/sessions/${sessionId}/shares/`,
    shareDetail: (sessionId: string, shareId: string) =>
      `/api/agents/chat/sessions/${sessionId}/shares/${shareId}/`,
    eligibleTargets: (sessionId: string) =>
      `/api/agents/chat/sessions/${sessionId}/eligible-targets/`,
    participants: (sessionId: string) =>
      `/api/agents/chat/sessions/${sessionId}/participants/`,
    transferOwnership: (sessionId: string) =>
      `/api/agents/chat/sessions/${sessionId}/transfer-ownership/`,
    sharedWithMe: '/api/agents/chat/sessions/shared-with-me/',
    unreadCount: '/api/agents/chat/sessions/unread-count/',
    markRead: (sessionId: string) =>
      `/api/agents/chat/sessions/${sessionId}/mark-read/`,
  },
  scheduler: {
    tools: '/api/scheduler/tools/',
    tasks: '/api/scheduler/tasks/',
    taskDetail: (id: string) => `/api/scheduler/tasks/${id}/`,
    runNow: (id: string) => `/api/scheduler/tasks/${id}/run-now/`,
    executions: (id: string) => `/api/scheduler/tasks/${id}/executions/`,
  },
  kb: {
    me: '/api/kb/me/',
    guide: '/api/kb/guide/',
    tree: '/api/kb/tree/',
    documents: '/api/kb/documents/',
    documentByPath: '/api/kb/documents/by-path/',
    documentMove: '/api/kb/documents/move/',
    documentArchive: '/api/kb/documents/archive/',
    documentDelete: '/api/kb/documents/delete/',
    documentUndelete: '/api/kb/documents/undelete/',
    excluidos: '/api/kb/excluidos/',
    documentUnarchive: '/api/kb/documents/unarchive/',
    documentHistory: '/api/kb/documents/history/',
    documentRestore: '/api/kb/documents/restore/',
    documentRevisionDiff: '/api/kb/documents/revision-diff/',
    search: '/api/kb/search/',
    export: '/api/kb/export/',
    drafts: '/api/kb/drafts/',
    draftsMine: '/api/kb/drafts/mine/',
    draftDetail: (id: string) => `/api/kb/drafts/${id}/`,
    draftApply: (id: string) => `/api/kb/drafts/${id}/apply/`,
    draftReject: (id: string) => `/api/kb/drafts/${id}/reject/`,
    grants: '/api/kb/grants/',
    grantsOverview: '/api/kb/grants/overview/',
    grantsEffective: '/api/kb/grants/effective/',
    grantsPreview: '/api/kb/grants/preview/',
    grantsGrupos: '/api/kb/grants/grupos/',
    grantDetail: (id: string) => `/api/kb/grants/${id}/`,
    activity: '/api/kb/activity/',
    reportsGaps: '/api/kb/reports/gaps/',
    reportsStaleness: '/api/kb/reports/staleness/',
    reportsBrokenLinks: '/api/kb/reports/broken-links/',
    folderMove: '/api/kb/folders/move/',
    folderLifecycle: '/api/kb/folders/lifecycle/',
  },
} as const

export default api
